import React, { useEffect, useState } from "react";
import usmApi from "@/lib/usmApi";
import { PageHeader } from "../UsmLayout";
import HubTabs from "../hub/HubTabs";
import { RefreshCw } from "lucide-react";

const ACTION_LABEL = {
  "branch.created": "Şube oluşturuldu",
  "branch.updated": "Şube güncellendi",
  "branch.deactivated": "Şube devre dışı bırakıldı",
  "branch.activated": "Şube aktifleştirildi",
  "employee.created": "Çalışan oluşturuldu",
  "employee.updated": "Çalışan güncellendi",
  "employee.activated": "Çalışan aktifleştirildi",
  "employee.deactivated": "Çalışan devre dışı bırakıldı",
  "employee.password_changed": "Çalışan şifresini değiştirdi",
  "employee.password_reset": "Çalışan şifresi sıfırlandı",
  "employee.permissions_updated": "Çalışan izinleri güncellendi",
  "inventory.created": "Ürün oluşturuldu",
  "inventory.updated": "Ürün güncellendi",
  "inventory.deleted": "Ürün silindi",
  "inventory.status_changed": "Ürün durumu değişti",
  "auth.logout": "Oturum kapatıldı",
  "sale.receipt_emailed": "Fiş e-posta ile gönderildi",
  "sale.receipt_email_failed": "Fiş e-posta hatası",
  "sale.receipt_downloaded": "Fiş PDF indirildi",
  "sale.receipt_shared_whatsapp": "Fiş WhatsApp ile paylaşıldı",
  "sale.warranty_corrected": "Garanti düzeltildi",
};

// Phase 54.15 — surface the receipt recipient email inline in the
// audit table so staff never have to expand a row to see who got the
// receipt. Any audit row whose action indicates an email operation
// exposes the recipient stored in `extra.recipient`; every other row
// renders `—` so the column stays lightweight.
const EMAIL_ACTIONS = new Set([
  "sale.receipt_emailed",
  "sale.receipt_email_failed",
]);

function recipientEmailFor(row) {
  if (!EMAIL_ACTIONS.has(row.action)) return null;
  return row?.extra?.recipient || row?.extra?.recipient_email || null;
}

export default function AuditList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [filter, setFilter] = useState({ action: "", target_type: "" });
  const [loading, setLoading] = useState(true);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.action) params.set("action", filter.action);
      if (filter.target_type) params.set("target_type", filter.target_type);
      params.set("page", p);
      params.set("limit", limit);
      const r = await usmApi.get(`/audit?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { /* surfaced by interceptor */ }
    finally { setLoading(false); }
  };

  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [filter.action, filter.target_type]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="usm-audit">
      <HubTabs section="system" />
      <PageHeader
        title="Denetim Günlüğü"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Denetim" }]}
      />

      <div className="bg-white border border-[#E7E5E4] mb-4 p-3 flex flex-wrap gap-3">
        <input data-testid="audit-action-filter" placeholder="Eylem ara (örn. inventory)" value={filter.action}
          onChange={(e) => setFilter((p) => ({ ...p, action: e.target.value }))}
          className="flex-1 min-w-[180px] px-3 py-2 border border-[#E7E5E4] text-sm" />
        <select value={filter.target_type} onChange={(e) => setFilter((p) => ({ ...p, target_type: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm">
          <option value="">Tüm hedefler</option>
          <option value="branch">Şube</option>
          <option value="employee">Çalışan</option>
          <option value="inventory_item">Envanter</option>
        </select>
        <button onClick={() => load()} className="px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]" title="Yenile"><RefreshCw size={14} /></button>
      </div>

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-4 py-3 w-44">Zaman</th>
              <th className="text-left px-4 py-3">Kullanıcı</th>
              <th className="text-left px-4 py-3">Eylem</th>
              <th className="text-left px-4 py-3">Hedef</th>
              <th className="text-left px-4 py-3">Değişiklikler</th>
              <th className="text-left px-4 py-3">Alıcı E-posta</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#71717A]">Yükleniyor...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">Kayıt bulunamadı</td></tr>
            ) : items.map((r) => {
              const recipient = recipientEmailFor(r);
              return (
              <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-t border-[#F5F5F4]">
                <td className="px-4 py-3 font-mono text-[11px] text-[#71717A]">{formatDate(r.at)}</td>
                <td className="px-4 py-3">{r.actor_username_snapshot || "system"}</td>
                <td className="px-4 py-3">
                  <span className="text-[#0A0A0A]">{ACTION_LABEL[r.action] || r.action}</span>
                  <div className="text-[10px] font-mono text-[#A1A1AA]">{r.action}</div>
                </td>
                <td className="px-4 py-3 text-[#52525B]">
                  <code className="text-[11px]">{r.target_type}</code>
                  <div className="text-[10px] text-[#A1A1AA] font-mono">{(r.target_id || "").slice(0, 8)}…</div>
                </td>
                <td className="px-4 py-3 text-[11px] text-[#52525B]">
                  {r.diff && Object.keys(r.diff).length > 0 ? (
                    <details>
                      <summary className="cursor-pointer text-[#C8A96A] hover:underline">{Object.keys(r.diff).length} alan</summary>
                      <pre className="bg-[#FAFAF9] mt-1 p-2 text-[10px] overflow-x-auto max-w-md">{JSON.stringify(r.diff, null, 2)}</pre>
                    </details>
                  ) : "—"}
                </td>
                <td
                  data-testid={`audit-recipient-${r.id}`}
                  className={"px-4 py-3 text-[11px] " + (recipient ? "font-mono text-[#0A0A0A]" : "text-[#A8A29E]")}
                >
                  {recipient || "—"}
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-[#71717A]">
          <span>Toplam {total} kayıt</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => { const np = page - 1; setPage(np); load(np); }} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">‹</button>
            <span>Sayfa {page}/{totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => { const np = page + 1; setPage(np); load(np); }} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">›</button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (e) { return iso; }
}
